import { Select } from 'shared/form/components/Select';
import { SelectOption } from 'shared/form/models/SelectOption';
import { useEffect, useMemo, useState } from 'react';
import { Cuenta } from 'core/cuenta/models/Cuenta';
import { useCoreService } from 'core/services/useCoreService';
import { useDataFetcher } from 'shared/http';
import { RegisterOptions, useController } from 'react-hook-form';
import { InputPlaceholder } from 'shared/placeholders';
import { SearchCuentasRequest } from 'core/cuenta/models/SearchCuentasRequests';

const DEFAULT_EMPTY_OPTION_LABEL_CLOSED = 'Todas las Cuentas';
const DEFAULT_EMPTY_OPTION_LABEL_OPEN =
  'Ingrese el numero o la denominacion de la cuenta';

interface CuentaSelectorProps {
  label: string;
  name: string;
  registerOptions?: RegisterOptions<any, any>;
  filtros?: SearchCuentasRequest;
  reloadToken?: number;
  /**
   * Si es true, la opción vacía muestra un texto de ayuda al abrir el menú
   * y vuelve al label cerrado al cerrarlo.
   */
  emptyOptionHintOnOpen?: boolean;
  /** Label de la opción vacía con el menú cerrado (default: "Todas las Cuentas"). */
  emptyOptionLabelClosed?: string;
  /** Label de la opción vacía con el menú abierto (solo con `emptyOptionHintOnOpen`). */
  emptyOptionLabelOpen?: string;
}

/**
 * Componente selector de Cuentas asíncrono.
 *
 * Actúa como un **Adaptador** entre la UI y el Formulario:
 * - **UI (Visual):** Muestra el objeto completo `{ label, value }` para que el usuario vea el nombre/número.
 * - **Formulario (Datos):** Guarda únicamente el `numero` (number) de la cuenta seleccionada.
 *
 * @component
 * @example
 * // Uso básico dentro de un formulario
 * <CuentaSelector
 * name="cuenta"
 * tipoCuenta={'tipoCuenta'} // Tipo cuenta por el cual se desea filtrar
 * registerOptions={{ required: "Este campo es obligatorio" }}
 * />
 *
 * @param {Object} props - Propiedades del componente.
 * @param {string} props.label - Label que tendrá el campo en el formulario (UI)
 * @param {string} props.name - Nombre del campo en el formulario (key en `data`).
 * @param {RegisterOptions} [props.registerOptions] - Reglas de validación de `react-hook-form` (ej. required, min, etc.).
 * @param {string} props.tipoCuenta - Tipo cuenta por el cual se realiza la búsqueda. Si es null se listan todas las cuentas.
 * @param {boolean} [props.emptyOptionHintOnOpen] - Cambia el label de la opción vacía al abrir el menú.
 *
 * @returns {JSX.Element} Un componente Select controlado o un Placeholder de carga.
 */
export const CuentaSelector = ({
  label,
  filtros,
  name,
  registerOptions,
  reloadToken = 0,
  emptyOptionHintOnOpen = false,
  emptyOptionLabelClosed = DEFAULT_EMPTY_OPTION_LABEL_CLOSED,
  emptyOptionLabelOpen = DEFAULT_EMPTY_OPTION_LABEL_OPEN,
}: CuentaSelectorProps): JSX.Element => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [cuentas, setCuentasRequest] = useDataFetcher<SelectOption<Cuenta>[]>([]);
  const coreService = useCoreService();
  const {
    field: { value, onChange },
    fieldState: { invalid },
    formState: { isSubmitted },
  } = useController({
    name,
    rules: registerOptions,
    defaultValue: null,
  });

  const filtrosEstables = useMemo(() => {
    return filtros;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filtros)]);

  useEffect(() => {
    const responseMapper = (lista: Cuenta[]): SelectOption<Cuenta>[] => {
      if (lista == null) {
        return [];
      }
      const result = lista.map((cuenta) => ({
        value: cuenta,
        label: cuenta.label,
      }));
      return result;
    };

    setCuentasRequest(() =>
      coreService
        .getVisibleAccounts(filtrosEstables)
        .then((response) => responseMapper(response)),
    );
  }, [coreService, setCuentasRequest, filtrosEstables, reloadToken]);

  const emptyOptionLabel = useMemo(() => {
    if (emptyOptionHintOnOpen && isMenuOpen) {
      return emptyOptionLabelOpen;
    }
    return emptyOptionLabelClosed;
  }, [
    emptyOptionHintOnOpen,
    isMenuOpen,
    emptyOptionLabelClosed,
    emptyOptionLabelOpen,
  ]);

  const cuentasOptions = useMemo(
    () => [{ value: null, label: emptyOptionLabel }, ...(cuentas?.data || [])],
    [cuentas?.data, emptyOptionLabel],
  );

  const selectedOption = useMemo(() => {
    return (
      cuentasOptions.find((option) => {
        if (!option.value) {
          return option.value === value;
        }
        return String(option.value.numeroCuenta) === String(value);
      }) || null
    );
  }, [cuentasOptions, value]);

  const handleSelection = (option: SelectOption<Cuenta>) => {
    onChange(option?.value?.numeroCuenta || null);
  };

  if (cuentas.isLoading) {
    return (
      <div className="mt-3 py-1">
        <InputPlaceholder />
      </div>
    );
  }

  return (
    <Select
      name="cuenta_selector"
      label={label}
      options={cuentasOptions}
      value={selectedOption}
      onChange={handleSelection}
      onMenuOpen={() => setIsMenuOpen(true)}
      onMenuClose={() => setIsMenuOpen(false)}
      {...(emptyOptionHintOnOpen
        ? { openMenuOnClick: true, openMenuOnFocus: true }
        : {})}
      classNames={{
        control: () => {
          if (!isSubmitted) {
            return '';
          }
          return invalid ? 'state-error' : 'state-valid';
        },
      }}
    />
  );
};
